import { ReactElement, useState } from 'react';
import Chart from 'react-apexcharts';
import { LINE_COLOR, PALETTE } from './plotConstants';
import { ApexOptions } from 'apexcharts';
import { useUpdateSession, useSetSession, useInvalidateSession } from '../../../../hooks/useSessions';

export default function LinePlot(props: {
  configs: any[];
  plotData: any;
  plotType?: string;
  session?: any;
  extraPrecision?: number;
}): ReactElement {
  const { configs, plotData, plotType, session } = props;

  // Handle null or undefined plotData
  if (!plotData || !plotData.configs || !plotData.plot_dict) {
    return <div className="cr-p-4 cr-text-gray-500">No data available</div>;
  }

  // Determine scale key and options based on plot type
  const getScaleConfig = () => {
    if (plotType === 'yaw_plot') {
      return {
        key: 'xw',
        options: [
          { value: 6, label: '6' },
          { value: 12, label: '12' },
          { value: 18, label: '18' },
          { value: 30, label: '30' },
          { value: 45, label: '45' },
          { value: 'auto', label: 'Auto' },
        ],
      };
    } else if (plotType === 'speed_plot') {
      return {
        key: 'speed',
        options: [
          { value: 3, label: '3' },
          { value: 6, label: '6' },
          { value: 9, label: '9' },
          { value: 12, label: '12' },
          { value: 'auto', label: 'Auto' },
        ],
      };
    }
    return null;
  };

  const scaleConfig = getScaleConfig();
  const scaleKey = scaleConfig?.key;

  // Initialize x-axis scale from session if available
  const initialXAxisScale =
    scaleKey && session?.plotting_scales?.[scaleKey] ? session.plotting_scales[scaleKey] : 'auto';
  const [xAxisScale, setXAxisScale] = useState<number | 'auto'>(initialXAxisScale);

  const updateSessionMutation = useUpdateSession(session?.id);
  const setSessionCache = useSetSession();
  const invalidateSession = useInvalidateSession(session?.id);

  // Handle X-axis scale changes
  const handleXAxisScaleChange = (newScale: number | 'auto') => {
    setXAxisScale(newScale);

    if (!session?.id || !scaleKey) return;

    const plottingScales = session.plotting_scales || {};

    if (newScale === 'auto') {
      // Remove the scale key if scale is 'auto'
      const remainingScales = { ...plottingScales };
      delete remainingScales[scaleKey];
      const updatedSession = {
        ...session,
        plotting_scales: remainingScales,
      };

      setSessionCache(updatedSession);

      updateSessionMutation.mutate(
        {
          plotting_scales: Object.keys(remainingScales).length > 0 ? remainingScales : {},
        },
        {
          onSuccess: () => {
            invalidateSession();
          },
        },
      );
    } else {
      // Set scale key to the numerical value
      const updatedSession = {
        ...session,
        plotting_scales: {
          ...plottingScales,
          [scaleKey]: newScale,
        },
      };

      setSessionCache(updatedSession);

      updateSessionMutation.mutate(
        {
          plotting_scales: {
            ...plottingScales,
            [scaleKey]: newScale,
          },
        },
        {
          onSuccess: () => {
            invalidateSession();
          },
        },
      );
    }
  };

  const plotConfigs = plotData.configs;
  const extents = plotData.extents;
  const colors = [];
  const dashArray = [];
  const markers = [];
  const lineData = [
    plotData.plot_dict.parabola[0].concat(plotData.plot_dict.line[0]),
    plotData.plot_dict.parabola[1].concat(plotData.plot_dict.line[1]),
  ];
  const line = lineData[0].map((value: number, index: number) => {
    const yValue = lineData[1][index];
    return [value, yValue];
  });
  const series: any = [];
  let configIndex = 0;
  let effectiveIndex = 0;
  for (const plotConfig of plotConfigs) {
    const data = [];
    const xValues = plotConfig[0];
    const yValues = plotConfig[1];
    for (const valueIndex in xValues) {
      const xValue = xValues[valueIndex];
      const yValue = yValues[valueIndex];
      data.push([xValue, yValue]);
    }
    const config = configs[configIndex];
    if (data.length > 0) {
      series.push({ name: config.letter, type: 'scatter', data });
      dashArray.push(0);
      markers.push(6);
      const color = PALETTE[effectiveIndex % PALETTE.length];
      colors.push(color);
      effectiveIndex++;
    }
    configIndex++;
  }
  series.push({ name: 'Normalisation Slope', type: 'line', data: line });
  dashArray.push(5);
  markers.push(0);
  colors.push(LINE_COLOR);

  const lineType = plotData.properties.plot_note[0];
  const notes = plotData.properties.plot_note[1].split('\n');
  const options: ApexOptions = {
    title: { text: `${plotData.properties.suptitle}: ${plotData.properties.title}`, align: 'left' },
    chart: { height: 350, zoom: { enabled: false }, type: 'line', animations: { enabled: false } },
    tooltip: { enabled: false },
    yaxis: {
      tickAmount: 7,
      min: extents[1][0],
      max: extents[1][1],
      labels: { formatter: (val: number) => val.toFixed(3) },
    },
    xaxis: {
      tickAmount: 15,
      min: extents[0][0],
      max: extents[0][1],
      labels: { formatter: (val: string) => parseFloat(val).toFixed(1) },
    },
    annotations: {
      yaxis: [
        {
          y: extents[1][1] - 0.05 * (extents[1][1] - extents[1][0]),
          width: '0%',
          label: {
            offsetX: 10,
            position: 'left',
            textAnchor: 'start',
            borderColor: LINE_COLOR,
            style: { color: '#fff', background: LINE_COLOR },
            text: lineType,
          },
        },
      ].concat(
        notes.map((note: string, index: number) => ({
          y: extents[1][0] + (index + 1) * 0.08 * (extents[1][1] - extents[1][0]),
          width: '0%',
          label: {
            offsetX: 10,
            position: 'left',
            textAnchor: 'start',
            borderColor: LINE_COLOR,
            style: { color: '#fff', background: LINE_COLOR },
            text: note,
          },
        })),
      ),
    },
    stroke: { width: 5, curve: 'straight', dashArray: dashArray },
    fill: { type: 'solid' },
    markers: { size: markers },
    colors: colors,
  };

  return (
    <div>
      <Chart options={options} series={series} type="line" width="100%" height="380" />
      {scaleConfig && (
        <div className="cr-mt-2 cr-flex cr-items-center cr-gap-2">
          <span className="cr-font-medium cr-text-gray-700">X-Axis Scale:</span>
          <select
            value={xAxisScale}
            onChange={(e) => {
              const newValue = e.target.value === 'auto' ? 'auto' : parseFloat(e.target.value);
              handleXAxisScaleChange(newValue);
            }}
            className="cr-cursor-pointer cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1"
          >
            {scaleConfig.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
